import {
  Bundle,
  BundlePagination,
  BundleSummary,
  CreateBundleComponentPayload,
  ListBundlesQuery,
  SetBundleComponentTourOfferPayload,
  UpdateBundlePayload,
  UpdateBundlePricesPayload,
} from '~/types/bundles/bundle';

import { json_headers, request } from './common';

interface OkResponse<T> {
  status: number;
  message: string;
  result: T;
}

function bundlesBasePath(): string {
  return `${window.configs.API_HOST}/api/bundle/v1/bundles`;
}

export async function listBundles(
  query: ListBundlesQuery,
  options?: { signal?: AbortSignal },
): Promise<{ bundles: Array<BundleSummary>; pagination: BundlePagination }> {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const response: OkResponse<Array<BundleSummary>> & { pagination: BundlePagination } = await request(
    `${bundlesBasePath()}?${search.toString()}`,
    { method: 'GET', signal: options?.signal },
  );

  return { bundles: response.result, pagination: response.pagination };
}

export async function getBundle(bundleId: string, options?: { signal?: AbortSignal }): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(`${bundlesBasePath()}/${bundleId}`, {
    method: 'GET',
    signal: options?.signal,
  });

  return response.result;
}

export async function createBundle(name: string): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(bundlesBasePath(), {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ name }),
  });

  return response.result;
}

export async function updateBundle(bundleId: string, payload: UpdateBundlePayload): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(`${bundlesBasePath()}/${bundleId}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  return response.result;
}

export async function createBundleComponent(bundleId: string, payload: CreateBundleComponentPayload): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(`${bundlesBasePath()}/${bundleId}/components`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  return response.result;
}

export async function deleteBundleComponent(bundleId: string, componentId: string): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(`${bundlesBasePath()}/${bundleId}/components/${componentId}`, {
    method: 'DELETE',
  });

  return response.result;
}

export async function setBundleDepartureDates(bundleId: string, dates: Array<string>): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(`${bundlesBasePath()}/${bundleId}/departure-dates`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ dates }),
  });

  return response.result;
}

export async function updateBundlePrices(bundleId: string, payload: UpdateBundlePricesPayload): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(`${bundlesBasePath()}/${bundleId}/prices`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  return response.result;
}

export async function setBundleComponentTourOffer(
  bundleId: string,
  componentId: string,
  payload: SetBundleComponentTourOfferPayload,
): Promise<Bundle> {
  const response: OkResponse<Bundle> = await request(
    `${bundlesBasePath()}/${bundleId}/components/${componentId}/tour-offer`,
    { method: 'PUT', headers: json_headers, body: JSON.stringify(payload) },
  );

  return response.result;
}
